import ExpandLessIcon from "@mui/icons-material/ExpandLessRounded";
import ExpandMoreIcon from "@mui/icons-material/ExpandMoreRounded";
import { Box, Collapse, IconButton, Stack, Typography } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  selectDimensionsForCurrentReportType,
  selectDimensionsStructureForCurrentReportType,
} from "../../../../../store/selectors/metaDataSelectors";
import type { FieldsSectionControl } from "../fieldsExpansionState.ts";
import FieldsSectionHeader from "../FieldsSectionHeader.tsx";
import DimensionsTree from "./DimensionsTree";

interface Props {
  searchText: string;
  disabled: boolean;
  section?: FieldsSectionControl;
}

const DimensionsList = ({ searchText, disabled, section }: Props) => {
  const dimensions = useSelector(selectDimensionsForCurrentReportType);
  const dimensionsStructure = useSelector(selectDimensionsStructureForCurrentReportType);
  const [dimensionsExpanded, setDimensionsExpanded] = useState(true);

  const revertDimensionExpanded = useCallback(() => setDimensionsExpanded(!dimensionsExpanded), [dimensionsExpanded]);
  const expanded = section?.open ?? dimensionsExpanded;

  const filteredDimensions = useMemo(() => {
    if (searchText === "") {
      return dimensions;
    }
    return dimensions.filter((d) => d.caption.toLowerCase().indexOf(searchText) > -1);
  }, [searchText, dimensions]);

  return (
    <Stack>
      {section ? (
        <FieldsSectionHeader title="Dimensions" open={section.open} onToggle={section.onToggle} />
      ) : (
        <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
          <Typography sx={{ flexGrow: 1, color: "text.primary" }} variant="subtitle2">
            Dimensions
          </Typography>
          <IconButton onClick={revertDimensionExpanded}>
            {dimensionsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
      )}
      <Collapse
        orientation="vertical"
        in={expanded}
        sx={{ "& .MuiCollapse-wrapperInner": { display: "block" }, ...(section && { pl: 2 }) }}
      >
        <DimensionsTree
          key={section?.groupsEpoch}
          fields={filteredDimensions}
          structure={dimensionsStructure}
          disabled={disabled}
          expanded={searchText !== "" || section?.groupsOpen === true}
          visible={expanded}
        />
      </Collapse>
    </Stack>
  );
};

export default DimensionsList;
